import { z } from 'zod';
import { SUPPORTED_LANGUAGES } from '../constants/languages.ts';
import { normalizeLocalizedText, type LocalizedText } from '../constants/localized-text.ts';
import {
  BASIS_POINTS_SCALE,
  DEFAULT_PAGE_SIZE,
  MAX_AMOUNT_CENTS,
  MAX_MEASURED_SQUARE_FEET,
  MAX_PAGE_SIZE,
  MAX_PRODUCTION_RATE_PER_HOUR,
  MIN_PRODUCTION_RATE_PER_HOUR,
} from '../constants/index.ts';
import { emailFormat } from './formats.ts';

/** ULIDs are 26 characters of Crockford base32. */
export const idSchema = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, 'Must be a valid identifier');

/** Trims, and turns an empty string into null so blank inputs clear a column. */
export const optionalText = (max = 500) =>
  z
    .string()
    .trim()
    .max(max, `Must be ${max} characters or fewer`)
    .transform((value) => (value.length === 0 ? null : value))
    .nullable()
    .optional();

export const requiredText = (max = 200, message = 'This field is required') =>
  z.string().trim().min(1, message).max(max, `Must be ${max} characters or fewer`);

/**
 * The same text in each language the company has filled in (see localized-text.ts).
 *
 * Keyed by `SUPPORTED_LANGUAGES`, so a payload naming a language this build
 * does not have is a 400 rather than a translation quietly stored and never
 * shown. Partial, because filling in one language and leaving the other for
 * later is the ordinary way an owner works through their library.
 *
 * Normalized on the way in for the same reason `optionalText` is: an entry
 * typed and then cleared arrives as `''`, and stored that way it would resolve
 * to an empty room heading instead of falling back to the base column the way
 * an untranslated row does. Everything blank makes the whole map `null`, which
 * is how a row goes back to being read in one language.
 */
export const localizedTextSchema = (max = 500) =>
  z
    .partialRecord(z.enum(SUPPORTED_LANGUAGES), z.string().trim().max(max, `Must be ${max} characters or fewer`))
    .transform((value): LocalizedText | null => normalizeLocalizedText(value))
    .nullable()
    .optional();

export const optionalEmail = z
  .union([z.literal(''), z.email(emailFormat('Enter a valid email address')).max(320)])
  .transform((value) => (value ? value.toLowerCase() : null))
  .nullable()
  .optional();

export const optionalUrl = z
  .union([z.literal(''), z.url('Enter a valid URL').max(500)])
  .transform((value) => (value ? value : null))
  .nullable()
  .optional();

/**
 * ISO 8601 calendar date, e.g. 2026-08-05.
 *
 * `Date.parse` silently rolls impossible dates forward — 2026-02-31 becomes
 * 3 March — so the parsed value is compared back against the input rather than
 * merely checked for NaN.
 */
export const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use the format YYYY-MM-DD')
  .refine((value) => {
    const parsed = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
  }, 'That date does not exist');

export const centsSchema = z
  .number()
  .int('Amounts are whole cents')
  .min(-MAX_AMOUNT_CENTS, 'Amount is out of range')
  .max(MAX_AMOUNT_CENTS, 'Amount is out of range');

export const nonNegativeCentsSchema = z
  .number()
  .int('Amounts are whole cents')
  .min(0, 'Cannot be negative')
  .max(MAX_AMOUNT_CENTS, 'Amount is out of range');

/**
 * The saved card a screen asked the office to confirm, as the screen showed it
 * ("Visa ending in 4242"): sent with a charge so a card replaced while the
 * question was open is refused rather than charged.
 */
export const confirmedCardLast4Schema = z.string().trim().min(1).max(8);
export const confirmedCardBrandSchema = z.string().trim().min(1).max(40);

export const minutesSchema = z
  .number()
  .int('Enter whole minutes')
  .min(0, 'Cannot be negative')
  .max(100_000, 'That is more than 1,600 hours');

export const basisPointsSchema = z
  .number()
  .int('Enter whole basis points')
  .min(0, 'Cannot be negative')
  .max(100_000, 'Cannot exceed 1000%');

/**
 * Basis points that are a share of something — a discount off a price, an
 * escalation of one — rather than a rate added on top, which is why this one
 * stops at 100%.
 *
 * That ceiling is also the CHECK on every column holding such a value
 * (`frequency_tier_*_discount_basis_points` in migration 0002,
 * `contract_escalation_basis_points` in the baseline), so a request that parses
 * is a request the database will accept. Left at the wider cap, a 150% tier
 * discount passed validation and then died on the constraint: a 500 where the
 * person deserved a 400 naming the field.
 */
export const percentBasisPointsSchema = z
  .number()
  .int('Enter whole basis points')
  .min(0, 'Cannot be negative')
  .max(BASIS_POINTS_SCALE, 'Cannot exceed 100%');

/**
 * A measured quantity: square feet of floor, or fixtures counted (0086).
 *
 * One schema for both, because both are whole non-negative counts and the cap
 * is a guard against a typo multiplying into money rather than a claim about
 * how big buildings get.
 */
export const measuredQuantitySchema = z
  .number()
  .int('Enter a whole number')
  .min(0, 'That cannot be negative')
  .max(MAX_MEASURED_SQUARE_FEET, 'That is larger than any building');

/**
 * A production rate, in units per hour.
 *
 * The floor is 1 rather than 0 because zero is not a slow job, it is a missing
 * answer — and dividing by it produces an infinity with no business reaching a
 * price.
 */
export const productionRateSchema = z
  .number()
  .int('Enter a whole number')
  .min(MIN_PRODUCTION_RATE_PER_HOUR, 'A production rate has to be at least 1')
  .max(MAX_PRODUCTION_RATE_PER_HOUR, 'That production rate is not believable');

export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
  q: z.string().trim().max(200).optional(),
  sort: z.string().trim().max(50).optional(),
});

export type PaginationQuery = z.infer<typeof paginationSchema>;

/**
 * Paging for the template libraries, which default to a full page rather than
 * a screenful.
 *
 * These lists are read to *populate a chooser*, not to be browsed: the quote
 * editor's room and service pickers ask for them and then show everything they
 * get. A company also starts with a seeded library of more than forty services,
 * so a 25-row default would mean a brand-new account's own standard templates
 * did not fit in one answer. The cap is unchanged — this only moves the
 * default to it, so the common case is one request instead of four.
 */
export const templatePaginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(MAX_PAGE_SIZE),
});

/**
 * Optimistic concurrency envelope (spec §11.2). Every mutation that touches a
 * quote carries the version the client last read.
 */
export const versionedSchema = z.object({
  version: z.number().int().min(1, 'A quote version is required'),
});
